// HomePage.jsx - Modern Tower Selection Page with Tabs

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Grid,
  Chip,
  alpha,
  Divider,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import InventoryIcon from "@mui/icons-material/Inventory";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SpaIcon from "@mui/icons-material/Spa";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BuildIcon from "@mui/icons-material/Build";
import MemoryIcon from "@mui/icons-material/Memory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CookieIcon from "@mui/icons-material/Cookie";
import FaceIcon from "@mui/icons-material/Face";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import SidebarLayout from "../shared/sections/sidebar/SidebarLayout";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/apiClient";
import { config } from "../assets/config";
import { useAppWorkspaceStatus } from "../hooks/useAppWorkspaceStatus";
import {
  APP_WORKSPACE_TOWERS,
  MECHANICAL_COMMODITY_ORDER,
  APP_WORKSPACE_MODEL_TYPE,
  filterCommoditiesByModelTab,
  getDefaultTowerModelTab,
  getTowerModelTabs,
  isPerlabParametricCommodity,
  isWorkspaceItemPublished,
} from "../utils/appWorkspaceConfig";

const towerIconMap = {
  mechanical: <PrecisionManufacturingIcon sx={{ fontSize: 28 }} />,
  electronics: <DeveloperBoardIcon sx={{ fontSize: 28 }} />,
  packaging: <InventoryIcon sx={{ fontSize: 28 }} />,
  fnb: <RestaurantIcon sx={{ fontSize: 28 }} />,
  bpch: <SpaIcon sx={{ fontSize: 28 }} />,
};

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { statusMap, loading: workspaceLoading, error: workspaceError, refreshStatuses } = useAppWorkspaceStatus();
  const workspaceReady = !workspaceLoading && !workspaceError;
  const brandColor = "#ff6034"; // updated to match new theme
  const [userName, setUserName] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [activeModelTab, setActiveModelTab] = useState(APP_WORKSPACE_MODEL_TYPE.AI_POWERED);
  const [requestNoticeOpen, setRequestNoticeOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoLoadFailed, setVideoLoadFailed] = useState(false);
  const normalizedRole = (() => {
    if (Array.isArray(user?.role) && user.role.length > 0) {
      return String(user.role[0]?.role_name || "")
        .trim()
        .toLowerCase();
    }
    return String(user?.role || "")
      .trim()
      .toLowerCase();
  })();
  const isSuperAdmin = normalizedRole === "super admin";
  const defaultVideoUrl =
    "https://www.youtube.com/embed/D0hZxXshtfM?rel=0&showinfo=0&modestbranding=1";

  // Check if user has access to a commodity
  const hasAccess = (towerTitle, commodityName) => {
    if (!user || !user.appAccess || !Array.isArray(user.appAccess) || user.appAccess.length === 0) {
      // If no app access data, require request access
      return false;
    }

    // Map tower titles to categories (exact match)
    const category = towerTitle;

    // Map commodity names to match app access format
    // App access format is "Category - Commodity"
    const accessKey = `${category} - ${commodityName}`;

    // Check if user has this exact access key
    return user.appAccess.includes(accessKey);
  };

  const getViewAccessList = () => {
    return Array.isArray(user?.viewAccess) ? user.viewAccess : null;
  };

  const viewAccessList = getViewAccessList();
  const applyViewFilter = Array.isArray(user?.viewAccess);
  const hasViewAccess = (towerTitle, commodityName) => {
    if (!applyViewFilter) return true;
    const accessKey = `${towerTitle} - ${commodityName}`;
    return (viewAccessList || []).includes(accessKey);
  };

  // PPT theme colors and typography (from NewTheme)
  const pptColors = {
    background: "#191A1B",
    surface: "#242728",
    border: "rgba(241, 242, 243, 0.08)",
    textPrimary: "#F1F2F3",
    textSecondary: "#798286",
    white: "#FFFFFF",
  };

  const pptTypography = {
    family: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
    title: {
      fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
      fontWeight: 100,
      fontSize: "40px",
      lineHeight: "40px",
      letterSpacing: "0.3px",
    },
    headline: {
      fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
      fontWeight: 500,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: "0.3px",
    },
    paragraph: {
      fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: "0.3px",
    },
  };

  useEffect(() => {
    if (user && user.firstName) {
      setUserName(user.firstName);
    } else {
      setUserName("User");
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const loadVideo = async () => {
      try {
        const response = await apiFetch("/api/video", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const data = await response.json();
        if (!cancelled && data?.url) {
          setVideoUrl(data.url);
          setVideoLoadFailed(false);
        }
      } catch (error) {
        console.error("Failed to load homepage video URL:", error);
        if (!cancelled) {
          setVideoLoadFailed(true);
        }
      }
    };

    loadVideo();
    return () => {
      cancelled = true;
    };
  }, []);

  const towers = APP_WORKSPACE_TOWERS.map((tower) => ({
    ...tower,
    icon: towerIconMap[tower.id],
  }));

  const visibleTowers = applyViewFilter
    ? towers
        .map((tower) => ({
          ...tower,
          commodities: tower.commodities.filter((commodity) =>
            hasViewAccess(tower.title, commodity.name),
          ),
        }))
        .filter((tower) => tower.commodities.length > 0)
    : towers;

  useEffect(() => {
    if (activeTab >= visibleTowers.length) {
      setActiveTab(0);
    }
  }, [activeTab, visibleTowers.length]);

  const activeTower = visibleTowers[activeTab];
  const activeTowerModelTabs = getTowerModelTabs(activeTower?.commodities || []);
  const activeTowerModelTabIds = activeTowerModelTabs.map((tab) => tab.id).join("|");
  const displayedCommodities = (() => {
    const filteredCommodities = filterCommoditiesByModelTab(
      activeTower?.commodities || [],
      activeModelTab,
    );

    if (activeTower?.id !== "mechanical") {
      return filteredCommodities;
    }

    return filteredCommodities.sort(
      (a, b) => MECHANICAL_COMMODITY_ORDER.indexOf(a.name) - MECHANICAL_COMMODITY_ORDER.indexOf(b.name),
    );
  })();

  useEffect(() => {
    if (!activeTower) {
      return;
    }

    setActiveModelTab(getDefaultTowerModelTab(activeTower.commodities));
  }, [activeTower?.id, activeTowerModelTabIds]);

  useEffect(() => {
    if (
      activeTowerModelTabs.length > 0 &&
      !activeTowerModelTabs.some((tab) => tab.id === activeModelTab)
    ) {
      setActiveModelTab(getDefaultTowerModelTab(activeTower?.commodities || []));
    }
  }, [activeModelTab, activeTower?.commodities, activeTowerModelTabs]);

  // Log user access info when user or activeTab changes
  useEffect(() => {
    if (user && towers && towers.length > 0) {
      const appAccess = user.appAccess || [];

      console.log("=".repeat(60));
      console.log("📋 COMMODITY ACCESS CHECK");
      console.log("=".repeat(60));
      console.log("👤 Logged in user:", user.email || `${user.firstName} ${user.lastName}`);
      console.log("🔑 User App Access:", appAccess);

      // Check all commodities across all towers
      const allDisabledCommodities = [];
      towers.forEach((tower) => {
        if (tower && tower.commodities && Array.isArray(tower.commodities)) {
          tower.commodities.forEach((commodity) => {
            if (commodity && commodity.name) {
              const accessKey = `${tower.title} - ${commodity.name}`;
              const hasAccessToCommodity = hasAccess(tower.title, commodity.name);
              if (!hasAccessToCommodity) {
                allDisabledCommodities.push(accessKey);
              }
            }
          });
        }
      });

      if (allDisabledCommodities.length > 0) {
        console.log("🚫 DISABLED COMMODITIES FOR USER:");
        allDisabledCommodities.forEach((commodity) => {
          console.log(`   - ${commodity}`);
        });
      } else {
        console.log("✅ User has access to all commodities");
      }
      console.log("=".repeat(60));
    }
  }, [user, activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleModelTabChange = (event, newValue) => {
    setActiveModelTab(newValue);
  };

  const handleRequestAccess = async (towerTitle, commodityName) => {
    try {
      const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
      const url = `${config.VITE_API_BASE_URL || ""}/api/admin/contact-support`;

      console.log("📧 Sending access request to:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_name: fullName || "N/A",
          user_email: user?.email || user?.username || "",
          tower: towerTitle,
          commodity: commodityName,
          request_type: "access_request",
        }),
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.error("❌ Server returned non-JSON response");
        alert("Failed to send access request. Please try again or contact support directly.");
        return;
      }

      const result = await response.json();

      if (response.ok) {
        setRequestNoticeOpen(true);
        console.log("✅ Access request sent:", result);
      } else {
        console.error("❌ Access request failed:", result);
        alert(result.error || "Failed to send access request. Please try again.");
      }
    } catch (error) {
      console.error("❌ Access request error:", error);
      alert(
        "Network error. Please try again or contact support directly at admin_perlab_pl@kearney.com",
      );
    }
  };

  const handleCommodityClick = (towerRoute, commodityName, commodity = null) => {
    // Map commodity names to specific routes for mechanical tower
    if (towerRoute === "/mechanical") {
      if (commodityName === "Casting & Machining") {
        navigate("/mechanical/casting");
        return;
      }

      if (commodityName === "Forging") {
        navigate("/mechanical/forging");
        return;
      }

      if (commodityName === "Sheet Metal") {
        // If hasAI flag is true, route to the copy version
        if (commodity?.hasAI) {
          navigate("/mechanical/sheetmetal?ai=true");
        } else {
          navigate("/mechanical/sheetmetal");
        }
        return;
      } else if (commodityName === "Plastics") {
        // Regular Plastics card (without hasAI) routes to copy version
        // Plastics card with hasAI routes to copy version with AI flag
        if (commodity?.hasAI) {
          navigate("/mechanical/costing?commodity=Plastics&ai=true");
        } else {
          // Regular Plastics card routes to copy version
          navigate("/mechanical/costing?commodity=Plastics");
        }
        return;
      }
    }

    // Map commodity names to specific routes for packaging tower
    if (towerRoute === "/packaging") {
      if (commodityName === "Laminates") {
        navigate("/packaging/laminates");
        return;
      } else if (commodityName === "Corrugated") {
        navigate("/packaging/costing");
        return;
      } else if (commodityName === "Bottles") {
        navigate("/packaging/glassbottles");
        return;
      }
    }

    // Route F&B and BPCH to generic costing page
    if (towerRoute === "/fnb") {
      navigate(`/fnb/generic?tower=FB&commodity=${encodeURIComponent(commodityName)}`);
      return;
    }

    if (towerRoute === "/bpch") {
      navigate(`/bpch/generic?tower=BPCH&commodity=${encodeURIComponent(commodityName)}`);
      return;
    }

    // Default behavior: Navigate to the tower's costing page with the selected commodity as a query parameter
    navigate(`${towerRoute}/costing?commodity=${encodeURIComponent(commodityName)}`);
  };

  const handleOpenVideoGuide = () => {
    window.dispatchEvent(new CustomEvent("velocity-help-guide-open", { detail: { index: 0 } }));
  };

  return (
    <SidebarLayout>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: pptColors.background,
          py: 4,
          "& .MuiTypography-root": {
            fontFamily: pptTypography.family,
            letterSpacing: "0.3px",
          },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {workspaceLoading && <Typography role="status" sx={{ mb: 2 }}>Loading available tools…</Typography>}
          {workspaceError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={refreshStatuses}>Retry</Button>}>
            Available tools could not be loaded. Retry to continue.
          </Alert>}
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "Column",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                ...pptTypography.headline,
                color: pptColors.white,
                mb: 1,
                fontSize: { xs: "1.25rem", md: "1.5rem" },
              }}
            >
              Welcome, {userName}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                ...pptTypography.title,
                color: pptColors.white,
                mb: 1.5,
                fontSize: { xs: "2rem", md: "2.5rem", lg: "3rem" },
                lineHeight: 1.2,
              }}
            >
              Velocity AI Costing Suite
            </Typography>
            <Typography
              variant="h6"
              sx={{
                ...pptTypography.paragraph,
                color: pptColors.textSecondary,
                fontWeight: 400,
                fontSize: { xs: "0.9rem", md: "1rem" },
                textAlign: "Left",
                maxWidth: "100%",
              }}
            >
              AI-Assisted parametric cost modeling enables intelligent cost estimation, optimization
              and uncover efficiency opportunities.
            </Typography>
          </Box>

          <Divider sx={{ mb: 3, borderColor: pptColors.border }} />

          <Box
            component="button"
            type="button"
            onClick={handleOpenVideoGuide}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1.5,
              px: { xs: 1.15, md: 1.3 },
              py: { xs: 0.6, md: 0.7 },
              border: `1px solid ${alpha(brandColor, 0.42)}`,
              borderLeft: `2px solid ${brandColor}`,
              borderRadius: "6px",
              background: `linear-gradient(90deg, ${alpha(brandColor, 0.18)} 0%, ${alpha(
                brandColor,
                0.045,
              )} 42%, rgba(36, 39, 40, 0.8) 100%)`,
              color: pptColors.textPrimary,
              cursor: "pointer",
              textAlign: "left",
              boxShadow: `0 6px 16px ${alpha("#000", 0.14)}`,
              transition: "border-color 0.18s ease, background-color 0.18s ease, transform 0.18s ease",
              "&:hover": {
                borderColor: brandColor,
                backgroundColor: alpha(brandColor, 0.12),
                transform: "translateY(-1px)",
              },
              "&:focus-visible": {
                outline: `2px solid ${brandColor}`,
                outlineOffset: 3,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "5px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: alpha(brandColor, 0.18),
                  color: brandColor,
                  border: `1px solid ${alpha(brandColor, 0.34)}`,
                }}
              >
                <OndemandVideoIcon sx={{ fontSize: 17 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    ...pptTypography.headline,
                    color: pptColors.textPrimary,
                    fontSize: { xs: "0.86rem", md: "0.95rem" },
                    fontWeight: 700,
                    lineHeight: 1.1,
                  }}
                >
                  Video Guide
                </Typography>
                <Typography
                  sx={{
                    ...pptTypography.paragraph,
                    mt: 0,
                    color: pptColors.textSecondary,
                    fontSize: { xs: "0.64rem", md: "0.7rem" },
                    lineHeight: 1.25,
                  }}
                >
                  Watch quick walkthroughs before starting your costing workflow.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* My Apps Section Title + Legend */}
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                ...pptTypography.headline,
                color: pptColors.textPrimary,
                fontSize: { xs: "1.25rem", md: "1.5rem" },
              }}
            >
              My Agents
            </Typography>

            {activeTowerModelTabs.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {activeTowerModelTabs.map((tab) => {
                  const isAiTab = tab.id === APP_WORKSPACE_MODEL_TYPE.AI_POWERED;

                  return (
                    <Box
                      key={tab.id}
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      {isAiTab ? (
                        <AutoAwesomeIcon sx={{ fontSize: "0.9rem", color: "#ff7b4a" }} />
                      ) : (
                        <AccountTreeIcon sx={{ fontSize: "0.9rem", color: "#22d3ee" }} />
                      )}
                      <Typography
                        sx={{
                          ...pptTypography.paragraph,
                          color: pptColors.textSecondary,
                          fontSize: "0.8rem",
                          lineHeight: 1.2,
                        }}
                      >
                        {tab.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ) : null}
          </Box>

          {/* Tabs Section */}
          <Box sx={{ mb: 3, borderBottom: "2px solid #494E50" }}>
            <Tabs
              value={visibleTowers.length > 0 ? activeTab : false}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#F1F2F3",
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                },
                "& .MuiTab-root": {
                  ...pptTypography.paragraph,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  minHeight: 56,
                  minWidth: 180,
                  maxWidth: 180,
                  color: pptColors.textSecondary,
                  transition: "all 0.3s ease",
                  flex: "0 0 180px",
                  "&.Mui-selected": {
                    color: "#F1F2F3",
                  },
                  "&:hover": {
                    color: "#F1F2F3",
                    backgroundColor: alpha("#F1F2F3", 0.08),
                  },
                },
              }}
            >
              {visibleTowers.map((tower) => (
                <Tab
                  key={tower.id}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {tower.icon}
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {tower.title}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {activeTowerModelTabs.length > 0 ? (
            <Box sx={{ mb: 3 }}>
              <Tabs
                value={activeModelTab}
                onChange={handleModelTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 44,
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#F1F2F3",
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                  },
                  "& .MuiTab-root": {
                    ...pptTypography.paragraph,
                    textTransform: "none",
                    minHeight: 44,
                    minWidth: "unset",
                    px: 0,
                    mr: 4,
                    color: pptColors.textSecondary,
                    alignItems: "flex-start",
                    "&.Mui-selected": {
                      color: pptColors.textPrimary,
                    },
                  },
                }}
              >
                {activeTowerModelTabs.map((tab) => (
                  <Tab
                    value={tab.id}
                    key={tab.label}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color:
                              tab.id === APP_WORKSPACE_MODEL_TYPE.AI_POWERED
                                ? "#ff7b4a"
                                : "#22d3ee",
                          }}
                        >
                          {tab.id === APP_WORKSPACE_MODEL_TYPE.AI_POWERED ? (
                            <AutoAwesomeIcon sx={{ fontSize: "0.9rem" }} />
                          ) : (
                            <AccountTreeIcon sx={{ fontSize: "0.9rem" }} />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            ...pptTypography.paragraph,
                            fontSize: "0.9rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {tab.label}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Box>
          ) : null}

          {/* Commodity Cards Grid */}
          <Box sx={{ flex: 1, mb: 4 }}>
            {visibleTowers.length === 0 ? (
              <Typography sx={{ color: pptColors.textSecondary }}>
                No sections available for your view access.
              </Typography>
            ) : displayedCommodities.length === 0 ? (
              <Typography sx={{ color: pptColors.textSecondary }}>
                No commodities available for this model.
              </Typography>
            ) : (
              <Grid container spacing={3}>
                {displayedCommodities.map((commodity, index) => {
                  const userHasAccess = hasAccess(activeTower.title, commodity.name);
                  const showPPMIcon = isPerlabParametricCommodity(commodity);
                  const commodityPublished = isWorkspaceItemPublished(
                    statusMap,
                    activeTower.title,
                    commodity.name,
                    commodity.modelType,
                  );
                  const canOpenComingSoon = !commodityPublished && isSuperAdmin;
                  const cardClickable = workspaceReady && ((commodityPublished && userHasAccess) || canOpenComingSoon);
                  const showHoverEffect = true;

                // Log disabled commodities
                if (!userHasAccess) {
                  const accessKey = `${activeTower.title} - ${commodity.name}`;
                  console.log(
                    `🚫 DISABLED: User "${
                      user?.email || "Unknown"
                    }" does not have access to "${accessKey}"`,
                  );
                }

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card
                      aria-disabled={!cardClickable}
                      onClick={() => {
                        if (cardClickable) {
                          handleCommodityClick(activeTower.route, commodity.name, commodity);
                        }
                      }}
                      sx={{
                        height: "100%",
                        width: "280px",
                        maxWidth: "100%",
                        cursor: cardClickable ? "pointer" : "default",
                        borderRadius: 3,
                        border: `2px solid ${pptColors.border}`,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative",
                        overflow: "hidden",
                        background: pptColors.surface,
                        boxShadow: `0 2px 8px ${alpha("#000", 0.2)}`,
                        opacity: userHasAccess ? 1 : 0.82,
                        "&:hover": showHoverEffect
                          ? {
                              transform: "translateY(-8px)",
                              boxShadow: `0 12px 24px ${alpha("#F1F2F3", 0.3)}`,
                              borderColor: "#F1F2F3",
                              "& .arrow-icon": {
                                transform: "translateX(4px)",
                                opacity: 1,
                              },
                            }
                          : {},
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 22,
                          height: 22,
                          px: 0,
                          borderRadius: showPPMIcon ? 1 : "50%",
                          backgroundColor: showPPMIcon ? "rgba(8, 145, 178, 0.14)" : "transparent",
                          border: "none",
                        }}
                      >
                        {showPPMIcon ? (
                          <AccountTreeIcon
                            sx={{
                              fontSize: "0.85rem",
                              color: "#22d3ee",
                            }}
                          />
                        ) : (
                          <AutoAwesomeIcon
                            sx={{
                              fontSize: "0.9rem",
                              color: "#ff7b4a",
                            }}
                          />
                        )}
                      </Box>
                      <CardContent
                        sx={{ p: 1.5, display: "flex", gap: 2, alignItems: "flex-start" }}
                      >
                        {/* Icon */}
                        <Box
                          className="commodity-icon"
                          sx={{
                            fontSize: "2rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 120,
                            height: 120,
                            minWidth: 120,
                            borderRadius: 2,
                            background: alpha(brandColor, 0.15),
                            transition: "all 0.3s ease",
                            overflow: "hidden",
                          }}
                        >
                          {typeof commodity.icon === "string" &&
                          commodity.icon.startsWith("/images") ? (
                            <img
                              src={commodity.icon}
                              alt={commodity.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            commodity.icon
                          )}
                        </Box>

                        {/* Text Content */}
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            minHeight: 120,
                          }}
                        >
                          {/* Title */}
                          <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  ...pptTypography.headline,
                                  mb: 0.5,
                                  color: pptColors.textPrimary,
                                  fontSize: "0.9rem",
                                }}
                              >
                                {commodity.name}
                              </Typography>
                            </Box>

                            {/* Description */}
                            <Typography
                              variant="body2"
                              sx={{
                                ...pptTypography.paragraph,
                                color: pptColors.textSecondary,
                                lineHeight: 1.4,
                                fontSize: "0.75rem",
                              }}
                            >
                              {commodity.description}
                            </Typography>
                          </Box>

                          {/* Learn More Link */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {!commodityPublished ? (
                              <Typography
                                variant="button"
                                sx={{
                                  ...pptTypography.paragraph,
                                  color: "#f5c04c",
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  letterSpacing: "0.4px",
                                }}
                              >
                                Coming Soon
                              </Typography>
                            ) : userHasAccess ? (
                              <Typography
                                variant="button"
                                sx={{
                                  ...pptTypography.paragraph,
                                  color: "#2ecc71",
                                  fontSize: "0.7rem",
                                  fontWeight: 500,
                                  letterSpacing: "0.4px",
                                  cursor: "pointer",
                                }}
                              >
                                Start Costing
                              </Typography>
                            ) : (
                              <Typography
                                variant="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleRequestAccess(activeTower.title, commodity.name);
                                }}
                                sx={{
                                  ...pptTypography.paragraph,
                                  color: brandColor,
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  letterSpacing: "0.4px",
                                  cursor: "pointer",
                                  "&:hover": {
                                    textDecoration: "none",
                                  },
                                }}
                              >
                                Request Access
                              </Typography>
                            )}
                            {commodityPublished ? (
                              <ArrowForwardIcon
                                className="arrow-icon"
                                sx={{
                                  fontSize: 16,
                                  color: userHasAccess ? "#2ecc71" : brandColor,
                                  transition: "all 0.3s ease",
                                  opacity: 0.7,
                                }}
                              />
                            ) : null}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
                })}
              </Grid>
            )}
          </Box>

          {/* Statistics Section - The Proof is in the Product */}
          <Box
            sx={{
              mb: 4,
              mt: 6,
              p: 4,
              borderRadius: 4,
              background: pptColors.surface,
              border: `1px solid ${pptColors.border}`,
              position: "relative",
              overflow: "hidden",
              boxShadow: `0 8px 32px ${alpha("#000", 0.3)}`,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(circle at top right, ${alpha(
                  brandColor,
                  0.1,
                )} 0%, transparent 50%)`,
                pointerEvents: "none",
              },
            }}
          >
            <Typography
              variant="h3"
              sx={{
                ...pptTypography.title,
                color: pptColors.textPrimary,
                mb: 5,
                textAlign: "center",
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                position: "relative",
                zIndex: 1,
                letterSpacing: "-0.5px",
              }}
            >
              The proof is in the product.
            </Typography>

            <Grid
              container
              spacing={4}
              sx={{
                mb: 4,
                position: "relative",
                zIndex: 1,
                justifyContent: "center",
                alignItems: "stretch",
                maxWidth: "1200px",
                mx: "auto",
              }}
            >
              {/* First Metric - Rounded Rectangle */}
              <Grid item xs={12} md={4} sx={{ display: "flex", flex: 1 }}>
                <Box
                  sx={{
                    p: 4,
                    width: "100%",
                    borderRadius: 4,
                    border: "2px solid",
                    borderColor: brandColor,
                    background: alpha(pptColors.surface, 0.6),
                    backdropFilter: "blur(10px)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(135deg, ${alpha(
                        brandColor,
                        0.1,
                      )} 0%, transparent 100%)`,
                      pointerEvents: "none",
                    },
                    "&:hover": {
                      transform: "translateY(-8px) scale(1.02)",
                      borderColor: "#ff7b4a",
                      boxShadow: `0 16px 48px ${alpha(brandColor, 0.3)}`,
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      ...pptTypography.title,
                      color: pptColors.textPrimary,
                      mb: 1.5,
                      fontSize: { xs: "2.5rem", md: "3.5rem" },
                      letterSpacing: "-2px",
                      position: "relative",
                    }}
                  >
                    1 million +
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      ...pptTypography.paragraph,
                      color: alpha(pptColors.textPrimary, 0.7),
                      fontWeight: 500,
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      letterSpacing: "0.5px",
                    }}
                  >
                    products optimized
                  </Typography>
                </Box>
              </Grid>

              {/* Second Metric - Rounded Rectangle (matching first) */}
              <Grid item xs={12} md={4} sx={{ display: "flex", flex: 1 }}>
                <Box
                  sx={{
                    p: 4,
                    width: "100%",
                    borderRadius: 4,
                    border: "2px solid",
                    borderColor: brandColor,
                    background: alpha(pptColors.surface, 0.6),
                    backdropFilter: "blur(10px)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, transparent 100%)",
                      pointerEvents: "none",
                    },
                    "&:hover": {
                      transform: "translateY(-8px) scale(1.02)",
                      borderColor: "#ff8555",
                      boxShadow: "0 16px 48px rgba(255, 107, 53, 0.3)",
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      color: pptColors.textPrimary,
                      mb: 1.5,
                      fontSize: { xs: "2.5rem", md: "3.5rem" },
                      letterSpacing: "-2px",
                      position: "relative",
                    }}
                  >
                    15x
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      fontWeight: 500,
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      letterSpacing: "0.5px",
                    }}
                  >
                    improved ROI
                  </Typography>
                </Box>
              </Grid>

              {/* Third Metric - Rounded Rectangle */}
              <Grid item xs={12} md={4} sx={{ display: "flex", flex: 1 }}>
                <Box
                  sx={{
                    p: 4,
                    width: "100%",
                    borderRadius: 4,
                    border: "2px solid",
                    borderColor: "#ff6b35",
                    background: "rgba(0, 0, 0, 0.3)",
                    backdropFilter: "blur(10px)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, transparent 100%)",
                      pointerEvents: "none",
                    },
                    "&:hover": {
                      transform: "translateY(-8px) scale(1.02)",
                      borderColor: "#ff8555",
                      boxShadow: "0 16px 48px rgba(255, 107, 53, 0.3)",
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      color: pptColors.textPrimary,
                      mb: 1.5,
                      fontSize: { xs: "2.5rem", md: "3.5rem" },
                      letterSpacing: "-2px",
                      position: "relative",
                    }}
                  >
                    320%
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      fontWeight: 500,
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      letterSpacing: "0.5px",
                    }}
                  >
                    quality improvement
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Bottom Description */}
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.85)",
                textAlign: "center",
                maxWidth: "900px",
                mx: "auto",
                lineHeight: 1.8,
                fontSize: { xs: "0.95rem", md: "1.05rem" },
                position: "relative",
                zIndex: 1,
                fontWeight: 400,
                pt: 4,
              }}
            >
              PERLab combines 100 years of Kearney's operational expertise with award-winning design
              and cutting-edge proprietary data tools to unlock tangible product gains.
            </Typography>
          </Box>

          {/* Video Section */}
          <Box
            sx={{
              mb: 4,
              mt: 4,
              p: 4,
              minHeight: 700,
              borderRadius: 4,
              background: pptColors.background,
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(circle at top right, ${alpha(
                  brandColor,
                  0.15,
                )} 0%, transparent 50%)`,
                pointerEvents: "none",
              },
            }}
          >
            {/* Video Container */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: "1000px",
                mx: "auto",
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 12px 40px rgba(0, 0, 0, 0.3)",
                aspectRatio: "16 / 9",
                minHeight: "500px",
                background: "#000",
                zIndex: 1,
                "&:hover": {
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.3)",
                  transition: "all 0.3s ease",
                },
              }}
            >
              {videoUrl && !videoLoadFailed ? (
                <video
                  controls
                  preload="metadata"
                  src={videoUrl}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <iframe
                  width="100%"
                  height="100%"
                  src={defaultVideoUrl}
                  title="Product Demo Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Design for Value Section */}
          <Box
            sx={{
              mb: 4,
              mt: 6,
              p: 5,
              borderRadius: 4,
              background: pptColors.background,
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(circle at top right, ${alpha(
                  brandColor,
                  0.15,
                )} 0%, transparent 50%)`,
                pointerEvents: "none",
              },
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: pptColors.textPrimary,
                mb: 5,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                position: "relative",
                zIndex: 1,
                letterSpacing: "-0.5px",
              }}
            >
              Design for value. Build for impact.
            </Typography>

            <Grid
              container
              spacing={4}
              sx={{
                position: "relative",
                zIndex: 1,
                justifyContent: "center",
                alignItems: "stretch",
                maxWidth: "1200px",
                mx: "auto",
              }}
            >
              {/* Card 1 - Design for growth */}
              <Grid item xs={12} md={4} sx={{ display: "flex", flex: 1 }}>
                <Box
                  sx={{
                    p: 4,
                    width: "100%",
                    minHeight: "350px",
                    borderRadius: 4,
                    border: "2px solid #ff6b35",
                    background: "rgba(0, 0, 0, 0.3)",
                    backdropFilter: "blur(10px)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    cursor: "pointer",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, transparent 100%)",
                      pointerEvents: "none",
                    },
                    "&:hover": {
                      transform: "translateY(-8px) scale(1.02)",
                      borderColor: "#ff8555",
                      boxShadow: "0 16px 48px rgba(255, 107, 53, 0.3)",
                      "& .icon-box": {
                        opacity: 0,
                      },
                      "& .hover-text": {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  {/* Icon and Title - Default State */}
                  <Box
                    className="icon-box"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      transition: "opacity 0.4s ease",
                      opacity: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    {/* Plant Icon */}
                    <Box
                      sx={{
                        fontSize: "4rem",
                        color: "#ff6b35",
                      }}
                    >
                      🌱
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color: pptColors.textPrimary,
                        fontSize: { xs: "1.5rem", md: "2rem" },
                      }}
                    >
                      Design for growth
                    </Typography>
                  </Box>

                  {/* Hover Text */}
                  <Box
                    className="hover-text"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      transition: "opacity 0.4s ease",
                      opacity: 0,
                      width: "90%",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: pptColors.textPrimary,
                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                        lineHeight: 1.6,
                      }}
                    >
                      2x faster time to market at half the cost
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Card 2 - Design for margin */}
              <Grid item xs={12} md={4} sx={{ display: "flex", flex: 1 }}>
                <Box
                  sx={{
                    p: 4,
                    width: "100%",
                    minHeight: "350px",
                    borderRadius: 4,
                    border: "2px solid #ff6b35",
                    background: "rgba(0, 0, 0, 0.3)",
                    backdropFilter: "blur(10px)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    cursor: "pointer",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, transparent 100%)",
                      pointerEvents: "none",
                    },
                    "&:hover": {
                      transform: "translateY(-8px) scale(1.02)",
                      borderColor: "#ff8555",
                      boxShadow: "0 16px 48px rgba(255, 107, 53, 0.3)",
                      "& .icon-box": {
                        opacity: 0,
                      },
                      "& .hover-text": {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  {/* Icon and Title - Default State */}
                  <Box
                    className="icon-box"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      transition: "opacity 0.4s ease",
                      opacity: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    {/* Cube Icon */}
                    <Box
                      sx={{
                        fontSize: "4rem",
                        color: "#ff6b35",
                      }}
                    >
                      📦
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color: pptColors.textPrimary,
                        fontSize: { xs: "1.5rem", md: "2rem" },
                      }}
                    >
                      Design for margin
                    </Typography>
                  </Box>

                  {/* Hover Text */}
                  <Box
                    className="hover-text"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      transition: "opacity 0.4s ease",
                      opacity: 0,
                      width: "90%",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: pptColors.textPrimary,
                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                        lineHeight: 1.6,
                      }}
                    >
                      {">60% cost reduction through smarter product redesign"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Card 3 - Build products people love */}
              <Grid item xs={12} md={4} sx={{ display: "flex", flex: 1 }}>
                <Box
                  sx={{
                    p: 4,
                    width: "100%",
                    minHeight: "350px",
                    borderRadius: 4,
                    border: "2px solid #ff6b35",
                    background: "rgba(0, 0, 0, 0.3)",
                    backdropFilter: "blur(10px)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    cursor: "pointer",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, transparent 100%)",
                      pointerEvents: "none",
                    },
                    "&:hover": {
                      transform: "translateY(-8px) scale(1.02)",
                      borderColor: "#ff8555",
                      boxShadow: "0 16px 48px rgba(255, 107, 53, 0.3)",
                      "& .icon-box": {
                        opacity: 0,
                      },
                      "& .hover-text": {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  {/* Icon and Title - Default State */}
                  <Box
                    className="icon-box"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      transition: "opacity 0.4s ease",
                      opacity: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    {/* Heart Icon */}
                    <Box
                      sx={{
                        fontSize: "4rem",
                        color: "#ff6b35",
                      }}
                    >
                      ❤️
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color: pptColors.textPrimary,
                        fontSize: { xs: "1.5rem", md: "2rem" },
                      }}
                    >
                      Build products people love
                    </Typography>
                  </Box>

                  {/* Hover Text */}
                  <Box
                    className="hover-text"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      transition: "opacity 0.4s ease",
                      opacity: 0,
                      width: "90%",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: pptColors.textPrimary,
                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                        lineHeight: 1.6,
                      }}
                    >
                      Move the needle with products that matter
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Footer Text */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: "auto",
              pt: 4,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontStyle: "italic",
                fontSize: "0.75rem",
              }}
            >
              © 2025 Kearney PERLab. All rights reserved.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontStyle: "italic",
                fontSize: "0.75rem",
              }}
            >
              Powered by advanced AI and ML models developed by PERLab's domain experts.
            </Typography>
          </Box>
        </Container>
        <Snackbar
          open={requestNoticeOpen}
          autoHideDuration={4000}
          onClose={() => setRequestNoticeOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => setRequestNoticeOpen(false)} severity="info" sx={{ width: "100%" }}>
            Request submitted.
          </Alert>
        </Snackbar>
      </Box>
    </SidebarLayout>
  );
};

export default HomePage;
